import {
  CANALES_OBRA,
  CONFIANZAS_OBRA,
  ETAPAS_OBRA,
  FASES_OBRA,
  INFLUENCIAS_OBRA,
  PRIORIDADES_OBRA,
  ROLES_CONTACTO_OBRA,
  TIPOS_ACCION_OBRA,
  TIPOS_APUNTE_OBRA,
  TIPOS_PROYECTO_OBRA,
} from '@matias-asset-os/contracts';
import type {
  AccionObra,
  ApunteObra,
  CanalObra,
  ConfianzaObra,
  ContactoObra,
  EtapaObra,
  FaseObra,
  InfluenciaObra,
  Obra,
  PrioridadObra,
  RolContactoObra,
  SituacionObra,
  TipoAccionObra,
  TipoApunteObra,
  TipoProyectoObra,
} from '@matias-asset-os/contracts';

/**
 * Oportunidades de obra: el modelo.
 *
 * EL MODELO VIVE EN LOS CONTRATOS, no aqui. Antes esto era una simulacion y el
 * modelo era de la pantalla; desde que las obras se guardan de verdad, la forma
 * la manda el contrato que comparten el navegador y el servidor. Aqui quedan
 * los nombres cortos con los que se lee el modulo y las funciones que calculan
 * cosas sobre una obra, que no son datos y no viajan.
 *
 * El modelo separa DOS DIMENSIONES que se confunden constantemente al hablar de
 * obras:
 *
 *   - Como va la CONSTRUCCION  -> `fase`, `progreso`, `finalizacionEstimada`
 *   - Como va la CONVERSACION  -> `etapa` del pipeline comercial
 *
 * Una obra terminada puede seguir siendo una oportunidad sin contactar, y una
 * obra en cimentacion puede tener ya una reunion programada. Mezclarlas es
 * exactamente lo que hace inutil una lista de oportunidades.
 */

export const FASES = FASES_OBRA;
export const CONFIANZAS = CONFIANZAS_OBRA;
export const ETAPAS = ETAPAS_OBRA;
export const TIPOS_PROYECTO = TIPOS_PROYECTO_OBRA;
export const ROLES_CONTACTO = ROLES_CONTACTO_OBRA;
export const INFLUENCIAS = INFLUENCIAS_OBRA;
export const CANALES = CANALES_OBRA;
export const TIPOS_ACCION = TIPOS_ACCION_OBRA;
export const PRIORIDADES = PRIORIDADES_OBRA;
export const TIPOS_APUNTE = TIPOS_APUNTE_OBRA;

export type Fase = FaseObra;
export type Confianza = ConfianzaObra;
export type Etapa = EtapaObra;
export type TipoProyecto = TipoProyectoObra;
export type RolContacto = RolContactoObra;
export type Influencia = InfluenciaObra;
export type Canal = CanalObra;
export type TipoAccion = TipoAccionObra;
export type Prioridad = PrioridadObra;
export type TipoApunte = TipoApunteObra;
export type Situacion = SituacionObra;
export type Contacto = ContactoObra;
export type Accion = AccionObra;
export type Apunte = ApunteObra;
export type { Obra };

/** Tres estados, no dos: `null` es «todavia no se sabe», que no es «no». */
export type Ternario = boolean | null;

export const SITUACION_VACIA: Situacion = {
  propietarioConocido: null,
  usoDelPropietario: null,
  saldraAVenta: null,
  yaEnVenta: null,
};

/** Etapas que ya no piden trabajo: no exigen proxima accion ni puntuan. */
export const ETAPAS_CERRADAS: readonly Etapa[] = ['GANADA', 'PERDIDA', 'PAUSA'];

export function etapaActiva(etapa: Etapa): boolean {
  return !ETAPAS_CERRADAS.includes(etapa);
}

/** Cuanto ha avanzado la conversacion, de 0 a 1. Alimenta la puntuacion. */
export const AVANCE_ETAPA: Record<Etapa, number> = {
  DETECTADA: 0,
  INVESTIGANDO: 0.15,
  PENDIENTE_CONTACTAR: 0.25,
  CONTACTADA: 0.45,
  CONVERSACION: 0.6,
  REUNION: 0.8,
  PROPUESTA: 0.95,
  GANADA: 1,
  PERDIDA: 0,
  PAUSA: 0.1,
};

// --- Frescura de la informacion ---------------------------------------------

/**
 * Cuando una obra deja de ser fiable. Los umbrales viven AQUI, con nombre,
 * porque son una decision de negocio que va a cambiar: repartidos por la
 * interfaz habria que buscarlos.
 */
export const UMBRALES_FRESCURA = {
  /** Hasta aqui, la informacion se da por buena. */
  recienteDias: 14,
  /** A partir de aqui conviene volver a mirar. */
  verificarDias: 30,
} as const;

export type Frescura = 'RECIENTE' | 'VERIFICAR' | 'DESACTUALIZADA' | 'SIN_VERIFICAR';

export function diasDesde(iso: string | null, hoy: string): number | null {
  if (!iso) return null;
  const a = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${hoy.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

export function frescuraDe(obra: Obra, hoy: string): Frescura {
  const dias = diasDesde(obra.ultimaComprobacion, hoy);
  if (dias === null) return 'SIN_VERIFICAR';
  if (dias <= UMBRALES_FRESCURA.recienteDias) return 'RECIENTE';
  if (dias <= UMBRALES_FRESCURA.verificarDias) return 'VERIFICAR';
  return 'DESACTUALIZADA';
}

// --- Estado de la proxima accion --------------------------------------------

export type EstadoAccion = 'VENCIDA' | 'HOY' | 'PROXIMA' | 'SIN_ACCION';

/** La primera accion pendiente, que es la unica que responde «y ahora que». */
export function proximaAccion(obra: Obra): Accion | null {
  const pendientes = obra.acciones
    .filter((a) => !a.hecha)
    .sort((a, b) => a.cuando.localeCompare(b.cuando));
  return pendientes[0] ?? null;
}

export function estadoAccion(obra: Obra, hoy: string): EstadoAccion {
  const accion = proximaAccion(obra);
  if (!accion) return 'SIN_ACCION';
  const dia = accion.cuando.slice(0, 10);
  const hoyDia = hoy.slice(0, 10);
  if (dia < hoyDia) return 'VENCIDA';
  if (dia === hoyDia) return 'HOY';
  return 'PROXIMA';
}

// --- Utilidades -------------------------------------------------------------

/** Dias que faltan para la finalizacion. Negativo si ya deberia estar hecha. */
export function diasHastaFin(obra: Obra, hoy: string): number | null {
  if (!obra.finalizacionEstimada) return null;
  const d = diasDesde(hoy, obra.finalizacionEstimada);
  return d;
}

export function tieneDecisor(obra: Obra): boolean {
  return obra.contactos.some((c) => c.influencia === 'DECIDE' && !c.sinContacto);
}

export function tieneContactoDirecto(obra: Obra): boolean {
  return obra.contactos.some((c) => !c.sinContacto && (c.telefono !== null || c.email !== null));
}
